/**
 * ArrivalsDataProvider - Listens for arrivals data requests from popups
 * 
 * This component runs in the main window and handles POPUP_INIT requests
 * for 'routes' type by fetching port arrivals or track data and responding.
 * Also fetches NextPort predictions when portLocode is available.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { usePortArrivals, useTracksGetList, useNextportPortGet, useBulkShipInfo } from '@ais-ui/api';
import { 
  usePopupMessages, 
  MainWindowActions, 
  MessageType,
  PopupMessage,
  PopupInitPayload,
} from './PopupEvents';
import { PopupType } from '@ais-ui/popups';
import { useSettings } from '../Settings/SettingsProvider';
import { usePermissions } from '../Permissions/PermissionsProvider';

interface ActiveRequest {
  popupId: string;
  portId?: number;
  portName?: string;
  portLocode?: string;
  shapeId?: string;
  shapeName?: string;
}

/** Enriched NextPort prediction with vessel info */
export interface EnrichedNextPortPrediction {
  imo: number;
  mmsi?: number;
  name?: string;
  eta: Date;
  probability: number;
  locode: string;
}

export const ArrivalsDataProvider = (): null => {
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
  const { settings } = useSettings();
  const permissions = usePermissions();
  const settingsRef = useRef(settings);
  const permissionsRef = useRef(permissions);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    permissionsRef.current = permissions;
  }, [permissions]);

  // Helper to get popup settings
  const getPopupSettings = () => ({
    tableDensity: settingsRef.current['ribbonPlugin.settings']?.table_density || 'compact',
  });

  // Fetch port arrivals when we have a port request
  const { data: portData, isLoading: isPortLoading, isError: isPortError } = usePortArrivals(
    activeRequest?.portId
  );

  // Fetch track data when we have a shape request
  const { data: trackData, isLoading: isTrackLoading, isError: isTrackError } = useTracksGetList(
    !!activeRequest?.shapeId
  );

  // Fetch NextPort predictions when we have a port locode and permission
  const shouldFetchNextport = !!activeRequest?.portLocode && permissionsRef.current?.nextport;
  const { data: nextportData, isLoading: isNextportLoading } = useNextportPortGet(
    shouldFetchNextport ? activeRequest?.portLocode || '' : ''
  );

  // Get IMOs from NextPort data for bulk ship info lookup
  const nextportImos = useMemo(
    () => nextportData?.records?.map((record) => record.imo) || [],
    [nextportData?.records]
  );

  // Fetch vessel info for NextPort predictions (names, MMSI)
  const { data: bulkShipData, isLoading: isBulkShipLoading } = useBulkShipInfo(
    shouldFetchNextport ? nextportImos : []
  );

  // Create a map of IMO -> ship info for quick lookup
  const shipInfoMap = useMemo(() => {
    if (!bulkShipData) return new Map<number, any>();
    const map = new Map<number, any>();
    bulkShipData.forEach((shipInfo) => {
      if (shipInfo.imo) {
        map.set(shipInfo.imo, shipInfo);
      }
    });
    return map;
  }, [bulkShipData]);

  // Build enriched NextPort predictions
  const enrichedNextportPredictions: EnrichedNextPortPrediction[] = useMemo(() => {
    if (!nextportData?.records) return [];
    return nextportData.records.map((record) => {
      const shipInfo = shipInfoMap.get(record.imo);
      const ship = shipInfo?.ship || shipInfo;
      return {
        imo: record.imo,
        mmsi: ship?.mmsi,
        name: ship?.name,
        eta: record.eta,
        probability: record.probability,
        locode: record.locode,
      };
    });
  }, [nextportData?.records, shipInfoMap]);

  const isLoading = activeRequest?.portId ? isPortLoading : isTrackLoading;
  const isError = activeRequest?.portId ? isPortError : isTrackError;
  
  // For NextPort, we wait for both the predictions and the ship info enrichment
  const isNextportReady = !shouldFetchNextport || (!isNextportLoading && !isBulkShipLoading);

  // Determine if we have the data we need
  const hasRequiredData = activeRequest
    ? (activeRequest.portId ? portData !== undefined : trackData !== undefined) && isNextportReady
    : false;

  // When data arrives, send it to the popup
  useEffect(() => {
    // Only respond when we have an active request, not loading, and have received data
    if (activeRequest && !isLoading && hasRequiredData) {
      const { popupId, portId, portName, portLocode, shapeId, shapeName } = activeRequest;
      
      // On error, don't send response - keep existing data in popup
      // The portserver can be fragile, so we prefer stale data over no data
      if (isError) {
        setActiveRequest(null);
        return;
      }
      
      let arrivals: any[] = [];
      
      if (portId && portData?.records) {
        arrivals = portData.records;
      } else if (shapeId && trackData?.records) {
        // Filter tracks by destination shape ID
        arrivals = trackData.records.filter(
          (record: any) => record.destination?.location?.id === shapeId
        );
      }

      // Generate dataKey based on arrivals content to prevent unnecessary re-renders
      const firstMmsi = arrivals[0]?.source?.ship?.mmsi || '';
      const lastMmsi = arrivals[arrivals.length - 1]?.source?.ship?.mmsi || '';
      const nextportCount = enrichedNextportPredictions.length;
      const dataKey = `${portId || shapeId}_${arrivals.length}_${firstMmsi}_${lastMmsi}_np${nextportCount}`;

      MainWindowActions.sendInitResponse(popupId, {
        arrivals,
        portId,
        portName,
        portLocode,
        shapeId,
        shapeName,
        settings: getPopupSettings(),
        dataKey,
        nextportPredictions: enrichedNextportPredictions,
        permissions: {
          nextport: permissionsRef.current?.nextport || false,
        },
      });
      
      // Clear active request after responding
      setActiveRequest(null);
    }
  }, [portData, trackData, isLoading, isError, activeRequest, hasRequiredData, enrichedNextportPredictions]);

  // Listen for routes requests
  usePopupMessages(useCallback((msg: PopupMessage) => {
    if (msg.type === MessageType.POPUP_INIT) {
      const payload = msg.payload as PopupInitPayload;
      
      if (payload.popupType === PopupType.Routes) {
        const params = payload.params as { 
          portId?: string | number;
          portName?: string;
          portLocode?: string;
          shapeId?: string;
          shapeName?: string;
        };
        
        // Set new active request - this will trigger the query
        setActiveRequest({
          popupId: payload.popupId,
          portId: params.portId ? Number(params.portId) : undefined,
          portName: params.portName,
          portLocode: params.portLocode,
          shapeId: params.shapeId,
          shapeName: params.shapeName,
        });
      }
    }
  }, []), []);

  return null;
};
